import { FEATURE_CONFIG } from '@/config/env';

type PerformanceMetricName =
  | 'fp'
  | 'fcp'
  | 'app-tti'
  | 'route-change'
  | 'dialog-open'
  | 'sheet-open'
  | 'drawer-open'
  | 'alert-dialog-open';

type PerformanceRating = 'excellent' | 'good' | 'qualified' | 'redline';

export interface FrontendPerformanceMetric {
  name: PerformanceMetricName;
  value: number;
  rating: PerformanceRating;
  path: string;
  timestamp: number;
  detail?: Record<string, unknown>;
}

declare global {
  interface Window {
    __LIGHTX_PERFORMANCE_REPORT__?: FrontendPerformanceMetric[];
  }
}

const METRIC_THRESHOLDS: Record<
  PerformanceMetricName,
  { excellent: number; good: number; qualified: number }
> = {
  fp: { excellent: 600, good: 1200, qualified: 1800 },
  fcp: { excellent: 600, good: 1200, qualified: 1800 },
  'app-tti': { excellent: 1500, good: 2200, qualified: 3500 },
  'route-change': { excellent: 300, good: 500, qualified: 800 },
  'dialog-open': { excellent: 150, good: 250, qualified: 400 },
  'sheet-open': { excellent: 150, good: 250, qualified: 400 },
  'drawer-open': { excellent: 150, good: 250, qualified: 400 },
  'alert-dialog-open': { excellent: 150, good: 250, qualified: 400 },
};

const overlayOpenStarts = new Map<PerformanceMetricName, number[]>();
let firstPaintReported = false;
let firstContentfulPaintReported = false;
let appTtiReported = false;
let routePerformanceInitialized = false;
let routeStartTime = 0;
let routeSequence = 0;
let measuredRouteSequence = -1;
let currentRouteIsInitial = true;

const getCurrentPath = () => {
  if (typeof window === 'undefined') return '';
  return (
    window.location.hash.replace(/^#/, '') || window.location.pathname + window.location.search
  );
};

const getRating = (name: PerformanceMetricName, value: number): PerformanceRating => {
  const thresholds = METRIC_THRESHOLDS[name];
  if (value <= thresholds.excellent) return 'excellent';
  if (value <= thresholds.good) return 'good';
  if (value <= thresholds.qualified) return 'qualified';
  return 'redline';
};

const roundMetricValue = (value: number) => Math.round(value * 100) / 100;

/**
 * 是否正在运行单元测试(vitest)。
 * vitest 执行测试时会把 import.meta.env.VITEST 置为 true，
 * 此时不应输出性能日志，避免刷屏、干扰测试控制台。
 * 注意：vite-env.d.ts 未声明 VITEST，这里通过类型断言安全读取。
 */
const isRunningTests = (): boolean =>
  Boolean((import.meta.env as ImportMetaEnv & { VITEST?: boolean }).VITEST);

const shouldLogMetric = () => {
  if (typeof window === 'undefined') return false;
  // 性能日志仅在「测试环境(test)」且「非运行测试」时输出：
  // - import.meta.env.MODE === 'test'：以 test 模式构建/预览（如 pnpm build:test、vite --mode test）
  // - !isRunningTests()：vitest 执行测试期间不输出，保持测试控制台干净
  return import.meta.env.MODE === 'test' && !isRunningTests();
};

export const recordPerformanceMetric = (
  name: PerformanceMetricName,
  value: number,
  detail?: Record<string, unknown>
) => {
  // VITE_ENABLE_PERF_ANALYSIS=false 时完全不运行性能检测代码，
  // 同时避免在 SSR / 非浏览器环境下读取 window 等对象导致报错。
  if (!FEATURE_CONFIG.enablePerfAnalysis) return;
  if (typeof window === 'undefined' || !Number.isFinite(value) || value < 0) return;

  const metric: FrontendPerformanceMetric = {
    name,
    value: roundMetricValue(value),
    rating: getRating(name, value),
    path: getCurrentPath(),
    timestamp: Date.now(),
    detail,
  };

  window.__LIGHTX_PERFORMANCE_REPORT__ = window.__LIGHTX_PERFORMANCE_REPORT__ || [];
  window.__LIGHTX_PERFORMANCE_REPORT__.push(metric);
  window.dispatchEvent(new CustomEvent('lightx:performance', { detail: metric }));

  if (shouldLogMetric()) {
    console.info('[performance]', metric);
  }
};

export const onNextPaint = (callback: () => void) => {
  if (typeof window === 'undefined') return;

  window.requestAnimationFrame(() => {
    window.requestAnimationFrame(callback);
  });
};

const onBrowserIdle = (callback: () => void, timeout = 3000) => {
  if (typeof window === 'undefined') return;

  if ('requestIdleCallback' in window) {
    window.requestIdleCallback(callback, { timeout });
    return;
  }

  globalThis.setTimeout(callback, 0);
};

export const setupFirstScreenPerformance = () => {
  if (!FEATURE_CONFIG.enablePerfAnalysis) return;
  if (typeof window === 'undefined' || typeof performance === 'undefined') return;

  const reportPaintEntry = (entry: PerformanceEntry) => {
    if (entry.name === 'first-paint' && !firstPaintReported) {
      firstPaintReported = true;
      recordPerformanceMetric('fp', entry.startTime, { source: 'PerformancePaintTiming' });
    }

    if (entry.name === 'first-contentful-paint' && !firstContentfulPaintReported) {
      firstContentfulPaintReported = true;
      recordPerformanceMetric('fcp', entry.startTime, { source: 'PerformancePaintTiming' });
    }
  };

  performance.getEntriesByType('paint').forEach(reportPaintEntry);

  if (!('PerformanceObserver' in window)) return;

  const observer = new PerformanceObserver((list) => {
    list.getEntries().forEach(reportPaintEntry);
  });

  try {
    observer.observe({ type: 'paint', buffered: true });
  } catch {
    observer.disconnect();
  }
};

export const setupRoutePerformance = () => {
  if (!FEATURE_CONFIG.enablePerfAnalysis) return;
  if (typeof window === 'undefined' || routePerformanceInitialized) return;

  routePerformanceInitialized = true;
  routeStartTime = 0;
  routeSequence = 0;
  measuredRouteSequence = -1;
  currentRouteIsInitial = true;

  window.addEventListener('hashchange', () => {
    routeStartTime = performance.now();
    routeSequence += 1;
    currentRouteIsInitial = false;
  });
};

export const markRouteRenderComplete = (detail?: Record<string, unknown>) => {
  if (!FEATURE_CONFIG.enablePerfAnalysis) return;
  if (typeof window === 'undefined' || measuredRouteSequence === routeSequence) return;

  const sequence = routeSequence;
  const startTime = routeStartTime;
  const isInitial = currentRouteIsInitial;
  measuredRouteSequence = sequence;

  onNextPaint(() => {
    const readyTime = performance.now();
    recordPerformanceMetric('route-change', readyTime - startTime, {
      ...detail,
      initial: isInitial,
    });

    if (!appTtiReported && isInitial) {
      onBrowserIdle(() => {
        appTtiReported = true;
        recordPerformanceMetric('app-tti', performance.now(), {
          source: 'initial-route-ready-and-browser-idle',
        });
      });
    }
  });
};

export const markOverlayOpenStart = (
  name: Extract<
    PerformanceMetricName,
    'dialog-open' | 'sheet-open' | 'drawer-open' | 'alert-dialog-open'
  >
) => {
  if (!FEATURE_CONFIG.enablePerfAnalysis) return;
  if (typeof window === 'undefined') return;

  const starts = overlayOpenStarts.get(name) || [];
  starts.push(performance.now());
  overlayOpenStarts.set(name, starts);
};

export const markOverlayOpenComplete = (
  name: Extract<
    PerformanceMetricName,
    'dialog-open' | 'sheet-open' | 'drawer-open' | 'alert-dialog-open'
  >,
  detail?: Record<string, unknown>
) => {
  if (!FEATURE_CONFIG.enablePerfAnalysis) return;
  if (typeof window === 'undefined') return;

  const starts = overlayOpenStarts.get(name) || [];
  const startTime = starts.shift() ?? performance.now();
  overlayOpenStarts.set(name, starts);

  onNextPaint(() => {
    recordPerformanceMetric(name, performance.now() - startTime, detail);
  });
};
